import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toCurrency } from "@helpers";
import { useChartColors } from "@hooks";

const calculatePercentOfTotal = ({
  holdingsValue = 0,
  fundsValue = 0,
  etfValue = 0,
  derivativeValue = 0,
  bondsValue = 0,
}: {
  holdingsValue?: number;
  fundsValue?: number;
  etfValue?: number;
  derivativeValue?: number;
  bondsValue?: number;
}) => {
  const totalValue =
    holdingsValue + fundsValue + etfValue + derivativeValue + bondsValue || 1;
  return {
    holdings: Math.round((holdingsValue * 100) / totalValue),
    funds: Math.round((fundsValue * 100) / totalValue),
    etf: Math.round((etfValue * 100) / totalValue),
    derivative: Math.round((derivativeValue * 100) / totalValue),
    bonds: Math.round((bondsValue * 100) / totalValue),
  };
};

interface AssetTypeStatistics {
  portfolioValue?: number;
  changeYTD?: number;
  changeTotalAbsolute?: number;
  hasData?: boolean;
}

interface UseAssetTypeDataParams {
  allHoldings: AssetTypeStatistics;
  funds: AssetTypeStatistics;
  etfs: AssetTypeStatistics;
  derivative: AssetTypeStatistics;
  bonds: AssetTypeStatistics;
}

export const useAssetTypeData = ({
  allHoldings,
  funds,
  etfs,
  derivative,
  bonds,
}: UseAssetTypeDataParams) => {
  const { t } = useTranslation();
  const chartColors = useChartColors("securitiesChart");

  const percents = calculatePercentOfTotal({
    holdingsValue: allHoldings?.portfolioValue,
    fundsValue: funds?.portfolioValue,
    etfValue: etfs?.portfolioValue,
    derivativeValue: derivative?.portfolioValue,
    bondsValue: bonds?.portfolioValue,
  });

  const assetTypeData = useMemo(
    () => [
      {
        title: t("assetTypeBreakdown.allHoldings.title"),
        amount: toCurrency(allHoldings.portfolioValue, "EUR"),
        increase: allHoldings.changeYTD,
        percent: percents.holdings,
        changeAbs: allHoldings.changeTotalAbsolute,
        color: chartColors.holdings,
        noDataText: t("assetTypeBreakdown.allHoldings.noData"),
        hasData: allHoldings.hasData,
      },
      {
        title: t("assetTypeBreakdown.funds.title"),
        amount: toCurrency(funds.portfolioValue, "EUR"),
        increase: funds.changeYTD,
        percent: percents.funds,
        changeAbs: funds.changeTotalAbsolute,
        color: chartColors.funds,
        noDataText: t("assetTypeBreakdown.funds.noData"),
        hasData: funds.hasData,
      },
      {
        title: t("assetTypeBreakdown.etfs.title"),
        amount: toCurrency(etfs.portfolioValue, "EUR"),
        increase: etfs.changeYTD,
        percent: percents.etf,
        changeAbs: etfs.changeTotalAbsolute,
        color: chartColors.etfs,
        noDataText: t("assetTypeBreakdown.etfs.noData"),
        hasData: etfs.hasData,
      },
      {
        title: t("assetTypeBreakdown.derivative.title"),
        amount: toCurrency(derivative.portfolioValue, "EUR"),
        increase: derivative.changeYTD,
        percent: percents.derivative,
        changeAbs: derivative.changeTotalAbsolute,
        color: chartColors.derivative,
        noDataText: t("assetTypeBreakdown.derivative.noData"),
        hasData: derivative.hasData,
      },
      {
        title: t("assetTypeBreakdown.bonds.title"),
        amount: toCurrency(bonds.portfolioValue, "EUR"),
        increase: bonds.changeYTD,
        percent: percents.bonds,
        changeAbs: bonds.changeTotalAbsolute,
        color: chartColors.bonds,
        noDataText: t("assetTypeBreakdown.bonds.noData"),
        hasData: bonds.hasData,
      },
    ],
    [allHoldings, funds, etfs, derivative, bonds, percents, chartColors, t]
  );

  return assetTypeData;
};
